<template>
  <div v-click-outside="closeSearchBox" class="search-box-two">
    <InputRoundedWButton
      :type="type"
      @text="searchedText = $event"
      @focused="inputFocused"
    />

    <transition name="collapse">
      <div
        v-if="showResponseDropdown"
        class="drop-down !w-[calc(100%-8px)] ltr:!left-1 rtl:!right-1"
      >
        <SearchResult
          result-list-class="max-xl:max-h-72 xl:max-h-96 overflow-y-scroll scroll-bar lighter"
          :searched-text="searchedText"
          :cities="cities"
          :search-result="searchResult"
          :loading="loading"
        />
      </div>
    </transition>

    <BaseBottomSheet
      fitHeight
      v-if="isMobileOrTablet"
      v-model:show="bottomSheetStatus"
    >
      <div>
        <div class="px-5 flex items-center gap-3">
          <LazySearchBoxTwoInput
            class="!w-[calc(100%-34px)]"
            :show-search="true"
            :auto-focus="focus"
            v-model:text="searchedText"
            @focused="inputFocused"
          />
          <span class="p-1" @click="bottomSheetStatus = false">
            <IconClose class="text-gray-1 text-[14px] mb-0 cursor-pointer" />
          </span>
        </div>
        <div class="result">
          <SearchResult
            result-list-class="max-h-[calc(76vh-123px)] overflow-y-scroll scroll-bar lighter"
            :searched-text="searchedText"
            :cities="cities"
            :search-result="searchResult"
            :loading="loading"
          />
        </div>
      </div>
    </BaseBottomSheet>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useAsyncData, useRoute } from "nuxt/app";
import type {
  IApiResponse,
  ITopCities,
  ISearchResult,
} from "../../types/general";
import customFetch from "../../composables/customFetch";
import useCustomFetch from "../../composables/useCustomFetch";
import SearchResult from "./SearchBoxTwoResult.vue";
import InputRoundedWButton from "./SearchBoxTwoInputRoundedWButton.vue";

const route = useRoute();

defineProps({
  type: { type: Number, default: 1}
})

const searchedText = ref<string>("");

const loading = ref<boolean>(false);
const searchResult = ref<Array<ISearchResult> | null>(null);
const showResponseDropdown = ref<boolean>(false);
const bottomSheetStatus = ref<boolean>(false);
const focus = ref<boolean>(false);

const { data: cities } = await useAsyncData<ITopCities[] | null>("top-cities", () =>
  customFetch(`api/top-cities`)
);

let timeout: ReturnType<typeof setTimeout>;

watch(searchedText, () => {
  if (searchedText.value.length > 2) {
    loading.value = true;
    if (timeout) {
      clearTimeout(timeout);
    }
    // Set a new timeout
    timeout = setTimeout(() => {
      getSearchResult();
    }, 1000);
  }
});

watch(
  () => route.path,
  () => {
    bottomSheetStatus.value = false;
    showResponseDropdown.value = false;
  }
);

const { isMobile: isMobileOrTablet } = useIsMobile(768);

async function getSearchResult() {
  searchResult.value = [];
  if (searchedText.value.length) {
    const { data, fetchData } = useCustomFetch(
      `/api/search?search=${searchedText.value}`
    );
    await fetchData();
    const result: IApiResponse<Array<ISearchResult>> | null =
      data.value as IApiResponse<Array<ISearchResult>> | null;
    searchResult.value = result?.data ? result.data : null;
  }
  loading.value = false;
}

const inputFocused = () => {
  if (isMobileOrTablet.value) {
    focus.value = true;
    bottomSheetStatus.value = true;
  } else {
    showResponseDropdown.value = true;
  }
};

const closeSearchBox = () => {
  showResponseDropdown.value = false;
  bottomSheetStatus.value = false;
  searchedText.value = "";
};

</script>
